import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import type { Task } from '../types/task';
import type { TaskStatusResult } from '../utils/taskStatus';
import { computeTaskStatus } from '../utils/taskStatus';
import ArchiveIcon from './icons/ArchiveIcon';
import UnarchiveIcon from './icons/UnarchiveIcon';

const STATUS_STYLES: Record<TaskStatusResult['variant'], { color: string; background: string; border: string }> = {
  open: { color: '#1f883d', background: '#defbe6', border: '#1f883d' },
  'partial-open': { color: '#1f883d', background: '#defbe6', border: '#1f883d' },
  merged: { color: '#8250df', background: '#f6f0ff', border: '#8250df' },
  draft: { color: '#57606a', background: '#f6f8fa', border: '#d0d7de' },
  closed: { color: '#cf222e', background: '#ffebe9', border: '#cf222e' },
  failed: { color: '#cf222e', background: '#ffebe9', border: '#cf222e' },
  running: { color: '#0969da', background: '#dbeafe', border: '#1d4ed8' },
};

const BADGE_WIDTH = 95;

function FailedStatusIcon({ color }: { color: string }) {
  return (
    <svg aria-hidden="true" width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path d="M3 3 9 9M9 3 3 9" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function GithubStatusIcon({ variant }: { variant: 'open' | 'partial-open' | 'closed' | 'merged' | 'draft' }) {
  const color = STATUS_STYLES[variant].color;
  if (variant === 'merged') {
    return (
      <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill={color}>
        <path d="M5.45 5.154A4.25 4.25 0 0 0 9.25 7.5h1.378a2.251 2.251 0 1 1 0 1.5H9.25A5.734 5.734 0 0 1 5 7.123v3.505a2.25 2.25 0 1 1-1.5 0V5.372a2.25 2.25 0 1 1 1.95-.218ZM4.25 13.5a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm8.5-4.5a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM5 3.25a.75.75 0 1 0 0 .005V3.25Z" />
      </svg>
    );
  }
  if (variant === 'draft') {
    return (
      <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill={color}>
        <path d="M3.25 1A2.25 2.25 0 0 1 4 5.372v5.256a2.251 2.251 0 1 1-1.5 0V5.372A2.251 2.251 0 0 1 3.25 1Zm9.5 14a2.25 2.25 0 1 1 0-4.5 2.25 2.25 0 0 1 0 4.5ZM2.5 3.25a.75.75 0 1 0 1.5 0 .75.75 0 0 0-1.5 0ZM3.25 12a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5Zm9.5 0a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5ZM14 7.5a1.25 1.25 0 1 1-2.5 0 1.25 1.25 0 0 1 2.5 0Zm0-4.25a1.25 1.25 0 1 1-2.5 0 1.25 1.25 0 0 1 2.5 0Z" />
      </svg>
    );
  }
  if (variant === 'closed') {
    return (
      <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill={color}>
        <path d="M3.25 1A2.25 2.25 0 0 1 4 5.372v5.256a2.251 2.251 0 1 1-1.5 0V5.372A2.251 2.251 0 0 1 3.25 1Zm9.5 5.5a.75.75 0 0 1 .75.75v3.378a2.251 2.251 0 1 1-1.5 0V7.25a.75.75 0 0 1 .75-.75Zm-2.03-5.273a.75.75 0 0 1 1.06 0l.97.97.97-.97a.748.748 0 0 1 1.265.332.75.75 0 0 1-.205.729l-.97.97.97.97a.751.751 0 0 1-.018 1.042.751.751 0 0 1-1.042.018l-.97-.97-.97.97a.749.749 0 0 1-1.275-.326.749.749 0 0 1 .215-.734l.97-.97-.97-.97a.75.75 0 0 1 0-1.06ZM2.5 3.25a.75.75 0 1 0 1.5 0 .75.75 0 0 0-1.5 0ZM3.25 12a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5Zm9.5 0a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5Z" />
      </svg>
    );
  }
  return (
    <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill={color}>
      <path d="M1.5 3.25a2.25 2.25 0 1 1 3 2.122v5.256a2.251 2.251 0 1 1-1.5 0V5.372A2.25 2.25 0 0 1 1.5 3.25Zm5.677-.177L9.573.677A.25.25 0 0 1 10 .854V2.5h1A2.5 2.5 0 0 1 13.5 5v5.628a2.251 2.251 0 1 1-1.5 0V5a1 1 0 0 0-1-1h-1v1.646a.25.25 0 0 1-.427.177L7.177 3.427a.25.25 0 0 1 0-.354ZM3.75 2.5a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5Zm0 9.5a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5Zm8.25.75a.75.75 0 1 0 1.5 0 .75.75 0 0 0-1.5 0Z" />
    </svg>
  );
}

function TaskStatusBadge({ status }: { status: TaskStatusResult | null }) {
  if (!status) {
    return null;
  }
  const style = STATUS_STYLES[status.variant];
  const showSpinner = status.variant === 'running';
  const isFailed = status.variant === 'failed';
  const isGithubStatus = status.variant === 'open' || status.variant === 'partial-open' || status.variant === 'merged' || status.variant === 'closed' || status.variant === 'draft';

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 8px',
        borderRadius: '999px',
        backgroundColor: style.background,
        border: `1px solid ${style.border}`,
        color: style.color,
        fontWeight: 600,
        fontSize: '12px',
        lineHeight: 1.2,
        minWidth: `${BADGE_WIDTH}px`,
        justifyContent: 'center',
        whiteSpace: 'nowrap',
      }}
    >
      {showSpinner ? (
        <span className="task-status-spinner" aria-hidden="true" />
      ) : isFailed ? (
        <FailedStatusIcon color={style.color} />
      ) : isGithubStatus ? (
        <GithubStatusIcon variant={status.variant as 'open' | 'partial-open' | 'closed' | 'merged' | 'draft'} />
      ) : (
        <span
          aria-hidden="true"
          style={{
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            backgroundColor: style.color,
          }}
        />
      )}
      <span>{status.label}</span>
    </span>
  );
}

interface TaskListProps {
  tasks: Task[];
  onLoadMore: () => void;
  hasMore: boolean;
  isLoading: boolean;
  hideHeader?: boolean;
  emptyMessage?: string;
  viewingArchived?: boolean;
  onArchiveToggle?: (task: Task, currentlyArchived: boolean) => void;
}



export default function TaskList({
  tasks,
  onLoadMore,
  hasMore,
  isLoading,
  hideHeader = false,
  emptyMessage = 'No tasks yet',
  viewingArchived = false,
  onArchiveToggle,
}: TaskListProps) {
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const lastTriggeredRef = useRef(false);
  const tasksLength = tasks.length;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          if (!hasMore) return;
          if (lastTriggeredRef.current) return;
          lastTriggeredRef.current = true;
          onLoadMore();
        });
      },
      { rootMargin: '200px 0px' },
    );
    observerRef.current = observer;
    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }
    return () => {
      observer.disconnect();
      observerRef.current = null;
    };
  }, [hasMore, onLoadMore]);

  useEffect(() => {
    lastTriggeredRef.current = false;
  }, [tasksLength]);

  const totalTasks = tasks.length;

  return (
    <div style={{ width: '100%', maxWidth: '1024px', margin: '20px auto' }}>
      {!hideHeader && <h2>Tasks</h2>}
      <div style={{ marginBottom: '12px' }}>
        <ul
          style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
          }}
        >
          {tasks.map((t) => {
            const taskStatus = computeTaskStatus(t);
            return (
              <li
                key={t.TaskID}
                className={'task-row'}
                style={{
                  padding: '8px 0',
                  borderBottom: '1px solid #ddd',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}
              >
                <Link
                  to={`/tasks/${t.TaskID}`}
                  style={{ textDecoration: 'none', color: 'inherit', flex: 1 }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ fontFamily: 'serif' }}>{t.Title}</div>
                  </div>
                </Link>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    minWidth: `${BADGE_WIDTH}px`,
                    marginLeft: '8px',
                    pointerEvents: 'none',
                  }}
                  aria-hidden
                >
                  <TaskStatusBadge status={taskStatus} />
                </div>
                {
                  <div className="task-action">
                    <button
                      type="button"
                      className="task-action-button"
                    data-tooltip={viewingArchived ? 'Unarchive' : 'Archive'}
                      aria-label={viewingArchived ? 'Unarchive' : 'Archive'}
                      onClick={(e) => {
                        e.preventDefault();
                        if (onArchiveToggle) {
                          onArchiveToggle(t, viewingArchived);
                        }
                      }}
                    >
                      {viewingArchived ? (
                        <UnarchiveIcon size={20} title="Unarchive" />
                      ) : (
                        <ArchiveIcon size={20} title="Archive" />
                      )}
                    </button>
                  </div>
                }
              </li>
          );
          })}
        </ul>
      </div>
      <div ref={sentinelRef} />
      {totalTasks === 0 && !isLoading && <div>{emptyMessage}</div>}
      {isLoading && <div style={{ padding: '8px 0', color: '#555' }}>Loading tasks…</div>}
    </div>
  );
}
